import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { productImage, onImgError } from "@/lib/image";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();

  const hasOptions = Array.isArray(product.options) && product.options.length > 0;
  const fromPrice = hasOptions
    ? Math.min(...product.options.map((o) => o.price))
    : product.price;

  const stock = typeof product.stock === "number" ? product.stock : null;
  const isClone = product.category === "clones";
  const isPreorder = isClone && (product.availability || "preorder") === "preorder";
  const soldOut = !isPreorder && stock !== null && stock <= 0;
  const lowStock = !isPreorder && stock !== null && stock > 0 && stock <= 5;

  const handleAdd = () => {
    if (soldOut) return;
    if (hasOptions) {
      const opt = [...product.options].sort((a, b) => a.price - b.price)[0];
      addItem(product, 1, {
        id: `${product.id}:${opt.quantity}`,
        name: `${product.name} — ${opt.label}`,
        price: opt.price,
      });
    } else {
      addItem(product);
    }
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group relative border border-white/5 bg-[#0F0F0F] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/30 hover:shadow-[0_8px_30px_rgba(16,185,129,0.1)] animate-fade-up"
      style={{ animationDelay: `${(index % 8) * 60}ms` }}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative aspect-square overflow-hidden bg-black">
          <img
            src={productImage(product.image_url)}
            onError={onImgError}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute top-3 left-3 label-caps text-[0.6rem] bg-black/60 backdrop-blur px-2.5 py-1 text-emerald-400 border border-emerald-500/20">
            {product.category === "clones" ? "Clone" : "Seeds"}
          </span>
          <span className="absolute top-3 right-3 label-caps text-[0.6rem] bg-black/60 backdrop-blur px-2.5 py-1 text-slate-300">
            {product.strain_type}
          </span>
          {isPreorder ? (
            <span data-testid={`stock-badge-${product.id}`} className="absolute bottom-3 left-3 label-caps text-[0.6rem] bg-amber-500/90 px-2.5 py-1 text-black font-semibold tracking-wide">
              Preorder
            </span>
          ) : soldOut ? (
            <span data-testid={`stock-badge-${product.id}`} className="absolute bottom-3 left-3 label-caps text-[0.6rem] bg-red-500/90 px-2.5 py-1 text-white font-semibold tracking-wide">
              Sold Out
            </span>
          ) : lowStock ? (
            <span data-testid={`stock-badge-${product.id}`} className="absolute bottom-3 left-3 label-caps text-[0.6rem] bg-amber-500/90 px-2.5 py-1 text-black font-semibold tracking-wide">
              Only {stock} left
            </span>
          ) : isClone ? (
            <span data-testid={`stock-badge-${product.id}`} className="absolute bottom-3 left-3 label-caps text-[0.6rem] bg-emerald-500/90 px-2.5 py-1 text-black font-semibold tracking-wide">
              Ready to Ship
            </span>
          ) : null}
        </div>
      </Link>

      <div className="p-5">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-display text-2xl leading-tight text-slate-100 group-hover:text-emerald-400 transition-colors">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
          <span>THC {product.thc}</span>
          <span className="w-1 h-1 rounded-full bg-slate-700" />
          <span>{product.flowering_time}</span>
        </div>
        {product.breeder && (
          <p className="text-[0.7rem] text-emerald-500/80 mt-1.5 truncate" data-testid={`product-breeder-${product.id}`}>
            {product.breeder}
          </p>
        )}
        <div className="flex items-center justify-between mt-5">
          <span data-testid={`product-price-${product.id}`} className="text-lg text-slate-100">
            {hasOptions ? `From $${fromPrice.toFixed(2)}` : `$${product.price.toFixed(2)}`}
          </span>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            disabled={soldOut}
            className="flex items-center gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors text-xs font-medium px-4 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-600"
          >
            <Plus size={14} /> {soldOut ? "Sold Out" : isPreorder ? "Preorder" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
